import { Spinner, Text } from "@chakra-ui/react";
import {
  usePagination,
  withPagination,
} from "../../contexts/PaginationContext";
import { Dropdown } from "../Dropdown";
import { useCallback, useEffect, useState } from "react";
import {
  assignCustomerToResidenceType,
  CustomerModel,
  getCustomerList,
  unassignCustomerFromResidenceType,
} from "../../clients/BackOfficeApi";
import { useIsAuthenticated } from "@azure/msal-react";
import { PaginationForm } from "../../elements/PaginationForm";

interface Props {
  residenceTypeId: number;
}

export const AssignedCustomers = withPagination<Props>(
  { settingsKey: "customers" },
  ({ residenceTypeId }) => {
    const isAuthenticated = useIsAuthenticated();
    const [customers, setCustomers] = useState<CustomerModel[]>([]);
    const [moreCustomersAvailable, setMoreCustomersAvailable] = useState(false);
    const { offset, limit } = usePagination();
    const [loadedOffset, setLoadedOffset] = useState(0);
    const [loading, setLoading] = useState(false);

    const fetchAssignedCustomers = useCallback(
      (refetch = false) => {
        if (!isAuthenticated || !residenceTypeId) return;

        const abortController = new AbortController();

        const query = refetch
          ? { Offset: 0, Limit: offset + limit, residenceTypeId }
          : { Offset: offset, Limit: limit, residenceTypeId };

        setLoading(true);

        getCustomerList({
          query,
          signal: abortController.signal,
          throwOnError: true,
        })
          .then(({ data }) => {
            if (abortController.signal.aborted) return;

            if (data?.items) {
              const loadedItems = data.items.length;
              setLoadedOffset(query.Offset + loadedItems);
              setCustomers((prev) => {
                prev.splice(query.Offset, loadedItems, ...(data.items ?? []));
                return prev.slice(0, query.Offset + loadedItems);
              });
              setMoreCustomersAvailable(
                data.totalCount! > query.Offset + loadedItems,
              );
            }
          })
          .catch((e) => {
            if (abortController.signal.aborted) return;

            throw e;
          })
          .finally(() => setLoading(false));

        return () =>
          abortController.abort("Component unmounted or re-rendered");
      },
      [isAuthenticated, residenceTypeId, offset, limit],
    );

    useEffect(() => fetchAssignedCustomers(), [fetchAssignedCustomers]);

    const assignCustomer = useCallback(
      async (customerId: number) => {
        if (!residenceTypeId) return;

        await assignCustomerToResidenceType({
          path: { residenceTypeId },
          query: { customerId },
          throwOnError: true,
        });

        fetchAssignedCustomers(true);
      },
      [residenceTypeId, fetchAssignedCustomers],
    );

    const unassignCustomer = useCallback(
      async (customerId: number) => {
        if (!residenceTypeId) return;

        await unassignCustomerFromResidenceType({
          path: { residenceTypeId, customerId },
          throwOnError: true,
        });

        fetchAssignedCustomers(true);
      },
      [residenceTypeId, fetchAssignedCustomers],
    );

    const toggleAssignedCustomer = useCallback(
      (customer: CustomerModel) =>
        customer.assignedResidenceTypeIds.includes(residenceTypeId)
          ? unassignCustomer(customer.id)
          : assignCustomer(customer.id),
      [residenceTypeId, assignCustomer, unassignCustomer],
    );

    if (!moreCustomersAvailable && loading) return <Spinner />;
    if (!customers?.length) return null;

    return (
      <Dropdown
        allowSearch
        footer={
          moreCustomersAvailable && (
            <PaginationForm loading={loading} offset={loadedOffset} />
          )
        }
        items={customers}
        maxW="sm"
        multiple
        name="assigned-customers-dropdown"
        onToggle={toggleAssignedCustomer}
        popoverWidth="sm"
        title="Toegewezen opdrachtgevers"
        value={customers.filter((c) =>
          c.assignedResidenceTypeIds.includes(residenceTypeId),
        )}
        valueDisplay={(selected) => {
          if (!selected.length) {
            return <Text color="brand.gray.500">Geen geselecteerd</Text>;
          }

          return (
            <Text>
              {selected.length === 1
                ? selected[0].name
                : `${selected.length} opdrachtgevers`}
            </Text>
          );
        }}
      />
    );
  },
);
